import type { paths } from '../../api/schema';
import type { Schemas } from '../../api/client';
import { api, unwrap } from '../../api/client';

/** Response body of a typed platform GET. */
type Ok<P extends keyof paths, M extends 'get' | 'post'> = paths[P][M] extends {
  responses: { 200: { content: { 'application/json': infer B } } };
}
  ? B
  : never;

export type Tenant = Schemas['PlatformTenant'];
export type TenantList = Schemas['PlatformTenantList'];
/**
 * Tenant detail. `supportAccess` arrives with M19; an older API omits it, so it is optional here.
 */
export type TenantDetail = Omit<Ok<'/platform/tenants/{id}', 'get'>, 'supportAccess'> & {
  supportAccess?: { active: boolean; expiresAt: string | null };
};
export type SubscriptionStatus = NonNullable<Schemas['SubscriptionStatus']>;
export type SubscriptionState = Schemas['SubscriptionState'];
export type SubscriptionEvent = Schemas['SubscriptionEvent'];
export type SubscriptionDetail = Ok<'/platform/tenants/{id}/subscription', 'get'>;
export type Plan = Schemas['SubscriptionPlan'];
export type TenantListQuery = NonNullable<paths['/platform/tenants']['get']['parameters']['query']>;

export const tenantKeys = {
  all: ['platform', 'tenants'] as const,
  list: (q: TenantListQuery) => ['platform', 'tenants', 'list', q] as const,
  detail: (id: string) => ['platform', 'tenants', 'detail', id] as const,
  subscription: (id: string) => ['platform', 'tenants', 'subscription', id] as const,
  support: (id: string) => ['platform', 'tenants', 'support', id] as const,
  plans: ['platform', 'plans'] as const,
};

export function fetchTenants(query: TenantListQuery) {
  return unwrap(api.GET('/platform/tenants', { params: { query } }));
}

export function fetchTenant(id: string): Promise<TenantDetail> {
  return unwrap(api.GET('/platform/tenants/{id}', { params: { path: { id } } }));
}

export function fetchSubscription(id: string) {
  return unwrap(api.GET('/platform/tenants/{id}/subscription', { params: { path: { id } } }));
}

export function fetchPlans() {
  return unwrap(api.GET('/platform/plans'));
}

export function fetchSupportSummary(id: string) {
  return unwrap(api.GET('/platform/tenants/{id}/support/summary', { params: { path: { id } } }));
}

/** Display status of a shop: closed / suspended win over the date-derived subscription state. */
export type DisplayStatus = SubscriptionState | 'SUSPENDED' | 'CLOSED' | 'NO_SUBSCRIPTION';

export function displayStatus(t: {
  status: Tenant['status'];
  subscription: SubscriptionStatus | null;
}): DisplayStatus {
  if (t.status === 'CLOSED') return 'CLOSED';
  if (t.subscription?.suspended || t.status === 'SUSPENDED') return 'SUSPENDED';
  return t.subscription?.state ?? 'NO_SUBSCRIPTION';
}

export const STATUS_LABELS: Record<DisplayStatus, string> = {
  ACTIVE: 'Active',
  EXPIRING_SOON: 'Expiring soon',
  GRACE: 'Grace period',
  LOCKED: 'Locked',
  SUSPENDED: 'Suspended',
  CLOSED: 'Closed',
  NO_SUBSCRIPTION: 'No subscription',
};

/**
 * A shop that has never paid (no plan renewal or redeemed code) is still on its trial. Extra days
 * given without a plan (EXTENDED) keep a trial shop on trial.
 */
/** The events taken back (deleted) by a platform admin. */
export function reversedIds(history: SubscriptionEvent[]): Set<string> {
  return new Set(
    history.flatMap((e) => (e.kind === 'REVERSED' && e.correctsEventId ? [e.correctsEventId] : [])),
  );
}

export function isTrial(history: SubscriptionEvent[]): boolean {
  const reversed = reversedIds(history);
  return !history.some((e) => ['RENEWED', 'CODE_REDEEMED'].includes(e.kind) && !reversed.has(e.id));
}

/** A renewal or extension as it stands now: the event, with its latest correction applied. */
export interface CorrectableEvent {
  event: SubscriptionEvent;
  days: number;
  paidThroughBefore: string;
  paidThroughAfter: string;
  paymentRef: string | null;
  notes: string | null;
}

/**
 * The entry a platform admin may edit or delete: the latest renewal or extension they entered,
 * when nothing after it moved the dates (the server checks the same). History is newest first.
 */
export function correctableEvent(history: SubscriptionEvent[]): CorrectableEvent | null {
  const reversed = reversedIds(history);
  const latest = history.find(
    (e) =>
      ['CREATED', 'RENEWED', 'EXTENDED', 'CODE_REDEEMED'].includes(e.kind) && !reversed.has(e.id),
  );
  if (!latest || latest.actorType !== 'PLATFORM_ADMIN') return null;
  if (latest.kind !== 'RENEWED' && latest.kind !== 'EXTENDED') return null;
  const fix = history.find((e) => e.kind === 'CORRECTED' && e.correctsEventId === latest.id);
  const now = fix ?? latest;
  return {
    event: latest,
    days: now.daysAdded ?? 0,
    paidThroughBefore: latest.paidThroughBefore,
    paidThroughAfter: now.paidThroughAfter,
    paymentRef: now.paymentRef,
    notes: now.notes,
  };
}

export const EVENT_LABELS: Record<SubscriptionEvent['kind'], string> = {
  CREATED: 'Trial started',
  RENEWED: 'Renewed',
  EXTENDED: 'Extended',
  CODE_REDEEMED: 'Code redeemed',
  SUSPENDED: 'Suspended',
  RESUMED: 'Resumed',
  UPDATED: 'Updated',
  CORRECTED: 'Edited',
  REVERSED: 'Deleted',
};

export const MIN_GRACE_DAYS = 1;
export const MAX_GRACE_DAYS = 15;
const DAY_MS = 86_400_000;

/** New paid-through after adding days: from max(paid-through, now), like the server (§1). */
export function extendedPaidThrough(paidThrough: string, days: number, now = Date.now()): Date {
  return new Date(Math.max(new Date(paidThrough).getTime(), now) + days * DAY_MS);
}

/** Signed whole days from now until an instant (negative = in the past). */
export function daysFromNow(value: string, now = Date.now()): number {
  return Math.round((new Date(value).getTime() - now) / DAY_MS);
}
